import type React from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import { Text } from "@/components/podkit/typography/Text";
import { Pill } from "@/components/podkit/status/Pill";
import { Button } from "@/components/flexkit/Button";
import { useSearchRepositoriesQuery } from "@/queries/runner-queries";
import { CornerDownLeft } from "lucide-react";
import { withGitpodSize } from "@/assets/icons/utils";
import { SearchMode, type SearchRepositoriesResponse_Repository } from "gitpod-next-api/gitpod/v1/runner_pb";
import type { PlainMessage } from "@bufbuild/protobuf";
import IconTasks from "central-icons/IconTasks";
import IconCrossSmall from "central-icons/IconCrossSmall";
import { useListRunnerSCMIntegrations } from "@/queries/runner-configuration-queries";
import { ErrorMessage } from "@/components/ErrorMessage";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { RepoTriggerSampleDialog } from "@/components/automations/RepoTriggerSampleDialog";
import { useEnvironmentClassEntries, type EnvironmentClassEntry } from "@/hooks/use-environment-class-entries";
import { Combobox } from "@/components/flexkit/combobox";

export const EnvironmentClassSelector: FC<{
    selectedEnvironmentClassId: string;
    disabled: boolean;
    onSelectionChange: (entry: EnvironmentClassEntry) => void;
}> = memo(({ selectedEnvironmentClassId, onSelectionChange }) => {
    const { environmentClassEntries, isLoading: isLoadingEnvironmentClasses } = useEnvironmentClassEntries({
        enabled: true,
    });

    const selectedEnvironmentClass = useMemo(
        () => environmentClassEntries.find((it) => it.clazz.id === selectedEnvironmentClassId),
        [environmentClassEntries, selectedEnvironmentClassId],
    );

    const _onSelectionChangeById = useCallback(
        (environmentClassId: string) => {
            const entry = environmentClassEntries.find((it) => it.clazz.id === environmentClassId);
            if (entry) {
                onSelectionChange(entry);
            }
        },
        [environmentClassEntries, onSelectionChange],
    );

    useEffect(() => {
        if (selectedEnvironmentClassId && environmentClassEntries.length > 0) {
            _onSelectionChangeById(selectedEnvironmentClassId);
        }
    }, [selectedEnvironmentClassId, _onSelectionChangeById, environmentClassEntries]);

    let comboboxContent: React.ReactNode;
    if (isLoadingEnvironmentClasses) {
        comboboxContent = <Combobox.Loading>Loading environment classes…</Combobox.Loading>;
    } else {
        comboboxContent = (
            <Combobox.List
                items={environmentClassEntries}
                searchKeys={["runner.name", "clazz.displayName", "clazz.description"]}
                noMatchesComponent={<Combobox.Empty>No matching environment classes.</Combobox.Empty>}
            >
                {(entry) => <EnvironmentClassSelectItem key={entry.clazz.id} entry={entry} />}
            </Combobox.List>
        );
    }

    return (
        <Combobox
            value={selectedEnvironmentClass?.clazz.id ?? ""}
            onValueChange={_onSelectionChangeById}
            loading={isLoadingEnvironmentClasses}
        >
            <Combobox.Value>
                {selectedEnvironmentClass ? (
                    <Combobox.ValueLabel>
                        {selectedEnvironmentClass.clazz.displayName}&nbsp;
                        <span className="text-content-muted">{selectedEnvironmentClass.clazz.description}</span>
                    </Combobox.ValueLabel>
                ) : (
                    <Combobox.ValueLabel className="text-content-muted">
                        Select an environment class
                    </Combobox.ValueLabel>
                )}
            </Combobox.Value>
            <Combobox.Popover className="w-128 max-w-[calc(100vw-1rem)]">{comboboxContent}</Combobox.Popover>
        </Combobox>
    );
});
EnvironmentClassSelector.displayName = "EnvironmentClassSelector";

const EnvironmentClassSelectItem: FC<{
    entry: EnvironmentClassEntry;
}> = memo(({ entry }) => {
    return (
        <Combobox.ListItem value={entry.clazz.id}>
            <Combobox.ListItemTitle>{entry.runner.name}</Combobox.ListItemTitle>
            <Combobox.ListItemDescription>
                {entry.clazz.displayName} • {entry.clazz.description}
            </Combobox.ListItemDescription>
        </Combobox.ListItem>
    );
});
EnvironmentClassSelectItem.displayName = "EnvironmentClassSelectItem";

export const RepoFilteringContent: FC<{
    repoSearchString: string;
    runnerId: string;
    disabled: boolean;
    scmHost: string;
    onScmHostChange: (scmHost: string) => void;
    onAddFilter: (filter: string) => void;
    onClearFilter: () => void;
}> = memo(({ repoSearchString, runnerId, scmHost: selectedScmHost, onAddFilter, onClearFilter, onScmHostChange }) => {
    const {
        data: scmIntegrations,
        isLoading: isLoadingSCMIntegrations,
        error: listSCMIntegrationsError,
        refetch: refetchSCMIntegrations,
    } = useListRunnerSCMIntegrations(runnerId);

    let scmHost: string;
    if (scmIntegrations && scmIntegrations.length > 0) {
        // find the SCM integration that matches the selected SCM host
        const selected = scmIntegrations.find((it) => it.host === selectedScmHost);
        if (selected) {
            scmHost = selected.host;
        } else {
            // if the selected SCM host is not found in the list of SCM integrations, use the first integration
            scmHost = scmIntegrations[0].host;
        }
    } else {
        scmHost = "";
    }

    const {
        data: repositories,
        isLoading: isSearching,
        error: searchRepositoriesError,
    } = useSearchRepositoriesQuery({
        runnerId,
        query: repoSearchString,
        limit: 25,
        scmHost,
        enabled: scmHost !== "" && repoSearchString !== "",
        searchMode: SearchMode.NATIVE,
    });
    const { toast } = useToast();

    useEffect(() => {
        if (searchRepositoriesError) {
            toast({
                title: "Error searching repositories",
                description: searchRepositoriesError.message,
            });
        }
    }, [searchRepositoriesError, toast]);

    useEffect(() => {
        if (scmIntegrations && scmIntegrations.length > 0 && !selectedScmHost) {
            // if the selected SCM host is not set, use the first scm integration found for the runner
            onScmHostChange(scmIntegrations[0].host);
        }
    }, [scmIntegrations, onScmHostChange, selectedScmHost]);

    if (isLoadingSCMIntegrations) {
        return (
            <Text className="animate-pulse text-base text-content-secondary">
                Finding suitable repository provider…
            </Text>
        );
    }

    if (listSCMIntegrationsError) {
        return (
            <div className="flex flex-row items-baseline gap-2">
                <ErrorMessage error={listSCMIntegrationsError} />
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                        void refetchSCMIntegrations();
                    }}
                    data-tracking-id="retry"
                >
                    Retry
                </Button>
            </div>
        );
    }

    if (isSearching) {
        return <Text className="animate-pulse text-base text-content-secondary">Searching repositories…</Text>;
    }

    if (repositories) {
        return <RepoFilterResult searchString={repoSearchString} repositories={repositories} onClear={onClearFilter} />;
    }

    if (scmIntegrations && scmIntegrations.length === 0) {
        return <Text>The selected runner does not support repository trigger. Please select a different runner.</Text>;
    }

    return <RepoFilterInput initialSearchString={repoSearchString} loading={isSearching} onAddFilter={onAddFilter} />;
});
RepoFilteringContent.displayName = "RepoFilteringContent";

const RepoFilterInput: FC<{ initialSearchString: string; loading: boolean; onAddFilter: (filter: string) => void }> =
    memo(({ initialSearchString, loading, onAddFilter }) => {
        const inputRef = useRef<HTMLInputElement>(null);

        const submitSearchString = () => {
            if (inputRef.current) {
                onAddFilter(inputRef.current.value);
            }
        };

        const handleKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter") {
                event.preventDefault();
                submitSearchString();
            }
        };

        return (
            <div className="flex w-full flex-row items-center gap-2">
                <input
                    ref={inputRef}
                    defaultValue={initialSearchString}
                    type="text"
                    placeholder="Describe what repositories to include or paste a GitHub filter"
                    className="h-9 flex-1 bg-transparent text-content-primary focus:outline-none focus:ring-0"
                    onKeyUp={handleKeyUp}
                />
                <Button
                    type="button"
                    variant="primary"
                    size="md"
                    className="aspect-square p-0"
                    loading={loading}
                    LeadingIcon={withGitpodSize(CornerDownLeft)}
                    onClick={submitSearchString}
                    data-tracking-id="submitsearchstring"
                >
                    <span className="sr-only">Add repository search filter</span>
                </Button>
            </div>
        );
    });
RepoFilterInput.displayName = "RepoFilterInput";

const RepoFilterResult: FC<{
    searchString: string;
    repositories: PlainMessage<SearchRepositoriesResponse_Repository>[];
    onClear: () => void;
}> = memo(({ searchString, repositories, onClear }) => {
    const [openRepoTriggerSampleDialog, setOpenRepoTriggerSampleDialog] = useState(false);

    return (
        <>
            <div className="flex flex-row items-center gap-2">
                <Pill variant="brand" size="md">
                    {searchString}
                </Pill>
                <Text className="flex-1 text-content-secondary">{repositories.length} matching repositories</Text>
                <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    LeadingIcon={withGitpodSize(IconTasks)}
                    data-tracking-id="code-context-repo-search-view-sample"
                    onClick={() => {
                        setOpenRepoTriggerSampleDialog(true);
                    }}
                >
                    View sample
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    LeadingIcon={withGitpodSize(IconCrossSmall)}
                    onClick={onClear}
                    data-tracking-id="code-context-repo-search-clear"
                >
                    Clear
                </Button>
            </div>
            <RepoTriggerSampleDialog
                repositories={repositories}
                open={openRepoTriggerSampleDialog}
                onOpenChange={setOpenRepoTriggerSampleDialog}
            />
        </>
    );
});
RepoFilterResult.displayName = "RepoFilterResult";
